// Datos personales en la URL, fuera de lo que el navegador manda a Sentry: trazas, errores,
// breadcrumbs y los eventos de URL del Session Replay.
//
// La búsqueda de socios (`/v1/clients/search/{club}?q=…`, `/v1/clients?q=…`) compara `q` con
// nombre, email, DNI y nº de socio, y ese texto viaja en la URL. Una traza del navegador
// describe cada `fetch` con su URL completa: sin este filtro, activar las trazas mandaría a
// Sentry lo que el backend ya tuvo que aprender a quitar (BE #677, 05-10-2026).
//
// DEFECTO-NEGAR y a propósito más burdo que el del backend: aquí NO se conserva ni una
// clave. Del backend ya sale la query filtrada con lo útil (qué ventana pidió); del navegador
// solo interesa el camino y cuánto tardó. Una clave puede ser el propio dato (`?X1234567L`).
//
// FALLA CERRADO: un evento demasiado anidado como para recorrerlo entero se descarta (`null`).
// Mandar sin mirar justo lo que no se pudo revisar sería el error que este filtro existe para
// evitar, y perder UNA traza no cuesta nada.
const FILTRADO = "[Filtered]";
const PROFUNDIDAD_MAXIMA = 40;

// Claves del evento cuyo valor ES una query (o la contiene suelta, sin `?` delante).
const CLAVES_CON_QUERY = new Set([
  "http.query",
  "url.query",
  "query",
  "query_string",
  "queryString",
]);

// `?…` dentro de cualquier texto: hasta el siguiente espacio o `#`. El fragmento se descarta
// con la query: `#` no lo manda el navegador al servidor y aquí no aporta nada.
const QUERY_EN_TEXTO = /\?[^\s#]*(#\S*)?/g;

// Tokens que viajan en el CAMINO de la URL y no en la query (la regex de arriba no los ve):
// el QR de una mesa (`/mesa/<token>`, `/v1/member/table/<token>`) y el de preinscripción de un
// club (`/pre-register/<token>`, `/v1/pre-register/<token>/submit`). Quien tenga el token puede
// abrir esa mesa o rellenar esa preinscripción: es una credencial, no un identificador.
// `scan-dni` es una ruta literal (`/v1/pre-register/scan-dni`), no un token: se conserva para que
// la traza diga QUÉ llamada fue. Un token nuevo en el camino hay que añadirlo aquí; el test
// `sentryTrazasSinQuery` recorre el código fuente y falla si encuentra uno sin cubrir.
const TOKEN_EN_RUTA =
  /(\/(?:mesa|member\/table|by-qr|pre-register)\/)(?!scan-dni(?:[/?#\s]|$))[^/?#\s]+/g;

export function enmascararTexto(texto) {
  let limpio = texto.includes("/") ? texto.replace(TOKEN_EN_RUTA, `$1${FILTRADO}`) : texto;
  if (limpio.includes("?")) limpio = limpio.replace(QUERY_EN_TEXTO, `?${FILTRADO}`);
  return limpio;
}

function recorrer(valor, profundidad) {
  if (profundidad > PROFUNDIDAD_MAXIMA) {
    throw new Error("evento demasiado anidado para enmascararlo");
  }
  if (typeof valor === "string") return enmascararTexto(valor);
  if (Array.isArray(valor)) return valor.map((v) => recorrer(v, profundidad + 1));
  if (valor && typeof valor === "object") {
    // Un objeto que no es un diccionario plano (Date, Map, instancia de clase) no se puede
    // recorrer con Object.entries sin perder su contenido: se filtra entero, no se deja pasar.
    const proto = Object.getPrototypeOf(valor);
    if (proto !== Object.prototype && proto !== null) return FILTRADO;
    const salida = {};
    for (const [clave, v] of Object.entries(valor)) {
      // la CLAVE también puede llevar una query (`GET /x?q=…` como clave de un mapa)
      const claveSegura = enmascararTexto(clave);
      salida[claveSegura] = CLAVES_CON_QUERY.has(clave) ? FILTRADO : recorrer(v, profundidad + 1);
    }
    return salida;
  }
  return valor;
}

/**
 * Devuelve el evento (traza, error, breadcrumb...) sin queries ni tokens de la URL, o `null` si no
 * pudo revisarlo entero. Sirve para `beforeSend`, `beforeSendTransaction` y `beforeBreadcrumb`.
 */
export function enmascararEvento(evento) {
  try {
    return recorrer(evento, 0);
  } catch (e) {
    return null;
  }
}

/**
 * `beforeAddRecordingEvent` del Session Replay. Solo los eventos que llevan URLs (breadcrumbs y
 * recursos de red, que el replay registra como eventos personalizados de tipo 5): los
 * instantáneas del DOM y sus mutaciones pesan cientos de KB y se recorrerían en cada cambio de
 * pantalla de una tablet, para nada — ya salen con el texto enmascarado (`maskAllText`).
 * Un evento de URL que no se pueda revisar se descarta (`null`).
 */
const ETIQUETAS_CON_URL = new Set(["breadcrumb", "performanceSpan"]);
export function enmascararEventoDelReplay(evento) {
  if (evento && evento.type === 5 && ETIQUETAS_CON_URL.has(evento.data && evento.data.tag)) {
    return enmascararEvento(evento);
  }
  return evento;
}
